import { Button, Text } from "@kvix/ui";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Theme,
  useTheme,
} from "@material-ui/core";
import * as Sentry from "@sentry/react";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import StripeInternal from "stripe";
import styled from "styled-components";
import { useIsMobile } from "../../../../hooks/isMobile";
import { PaymentContext } from "../context";
import {
  useAvailablePlansForSubscription,
  useScheduleWithUpcomingChanges,
} from "../hooks";
import { getPlanDetails } from "../i18n";
import { SubscriptionScheduleInformation } from "./UpcomingPlanChangeAlert";

interface Props {
  subscription: StripeInternal.Subscription;
  open: boolean;
  onClose: () => void;
}

const PlanRadioButton: React.FC<{ plan: StripeInternal.Plan }> = (props) => {
  const { t, ready } = useTranslation();
  const details = getPlanDetails(props.plan);

  if (!details || !ready) {
    return null;
  }

  return (
    <FormControlLabel
      value={props.plan.id}
      control={<Radio />}
      style={{ marginRight: 24 }}
      label={<>{t(details.title)}</>}
    />
  );
};

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  pointer-events: none;

  background-color: ${({ theme }) =>
    (theme as Theme).palette.type === "light"
      ? `rgb(255 255 255 / 80%)`
      : `rgb(0 0 0 / 50%)`};
`;

const ChangeSubscriptionPlan: React.FC<Omit<Props, "open">> = (props) => {
  const { t, ready } = useTranslation(["payment", "general"]);
  const { actions } = useContext(PaymentContext);
  const [submitting, setSubmitting] = useState(false);
  const [newPlanId, setNewPlanId] = useState(
    props.subscription.items.data[0].price.id
  );
  const theme = useTheme();

  const filteredPlans = useAvailablePlansForSubscription(props.subscription);

  const schedule = useScheduleWithUpcomingChanges(props.subscription);

  const submit = async () => {
    setSubmitting(true);

    try {
      await actions.changeSubscriptionPlan(props.subscription, newPlanId);
      props.onClose();
    } catch (error) {
      Sentry.captureException(error, { tags: { package: "client" } });
      console.error(error);
    }

    setSubmitting(false);
  };

  if (!ready) {
    return null;
  }

  return (
    <>
      <DialogContent style={{ position: "relative" }}>
        <Text variant="h4" gutterBottom>
          {t("payment:dialog.editBillingInterval.title")}
        </Text>

        <Box mb={3}>
          <Text variant="body2">
            {t("payment:dialog.editBillingInterval.description")}
          </Text>
        </Box>

        <FormControl
          disabled={schedule.loading || submitting || !!schedule.value}
          component="fieldset"
        >
          <FormLabel component="legend">
            {t("payment:billingInterval")}
          </FormLabel>
          <RadioGroup
            row
            name="interval"
            value={newPlanId}
            onChange={(event) => setNewPlanId(event.target.value)}
          >
            {filteredPlans.map((plan) => (
              <PlanRadioButton key={plan.id} plan={plan} />
            ))}
          </RadioGroup>
        </FormControl>

        {schedule.value && (
          <Box pt={2} pb={1}>
            <SubscriptionScheduleInformation
              activePlanIds={schedule.value.activePlanIds}
              upcomingPlanIds={schedule.value.upcomingPlanIds}
              loading={schedule.cancelLoading}
              onCancel={schedule.cancel}
            />
          </Box>
        )}

        <Fade in={schedule.loading}>
          <LoadingOverlay theme={theme}>
            <CircularProgress size={60} />
          </LoadingOverlay>
        </Fade>
      </DialogContent>

      <DialogActions>
        <Button
          color="primary"
          variant="text"
          onClick={props.onClose}
          disabled={submitting}
        >
          {t("general:buttons.goBack")}
        </Button>

        <Button
          color="primary"
          onClick={submit}
          disabled={
            !newPlanId ||
            newPlanId === props.subscription.items.data[0].price.id
          }
          loading={submitting}
        >
          {t("general:buttons.save")}
        </Button>
      </DialogActions>
    </>
  );
};

export const EditSubscriptionModal: React.FC<Props> = (props) => {
  const isMobile = useIsMobile();

  return (
    <Dialog
      onEscapeKeyDown={props.onClose}
      onBackdropClick={props.onClose}
      open={props.open}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <ChangeSubscriptionPlan
        onClose={props.onClose}
        subscription={props.subscription}
      />
    </Dialog>
  );
};
